import { AxiosError } from 'axios'

import {
  ApiStandardBatteryResponse,
  ApiTestMeasureListResponse,
  ApiTestPlan,
  ApiTestPlanInput,
  toApiGradeLevel,
  toApiGradeLevelValue,
  toApiMeasures,
  toApiModality,
  toNotStartedSummary,
  toNpTestPlanMeasure,
  toNpTestPlanSummary,
} from './npTestPlanApiMappers'
import {
  CreateNpTestPlanArgs,
  NpTestPlanMeasure,
  NpTestPlanMeasureEntry,
  NpTestPlanModality,
  NpTestPlanSummary,
} from './npTestPlanTypes'
import { baseApi, RTKQueryTag } from '../../../data/baseApi'

type GetNpTestPlanSummaryArgs = {
  episodeId: string
  patientId: string
}

type UpdateNpTestPlanMeasuresArgs = {
  episodeId: string
  measures: NpTestPlanMeasureEntry[]
}

type DuplicateNpTestPlanContactGroupArgs = {
  planId: string
  sourceGroupId: string
  /** Picked by the client. LyraTherapy rejects an id the plan already has. */
  contactGroupId: string
}

type RemoveNpTestPlanContactGroupArgs = {
  planId: string
  contactGroupId: string
}

const testPlanUrl = (episodeId: string) => `/lt/v1/episodes/${episodeId}/test_plan`

const contactGroupsUrl = (planId: string) => `/lt/v1/test_plans/${planId}/contact_groups`

const toSummary = (plan: unknown) => toNpTestPlanSummary(plan as ApiTestPlan)

const npTestPlansApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNpTestPlanSummary: builder.query<NpTestPlanSummary, GetNpTestPlanSummaryArgs>({
      queryFn: async (args, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({ url: testPlanUrl(args.episodeId), ignore404: true })
        if (result.error) {
          return (result.error as AxiosError).response?.status === 404
            ? { data: toNotStartedSummary(args) }
            : { error: result.error }
        }
        return { data: toSummary(result.data) }
      },
      providesTags: [RTKQueryTag.NP_TEST_PLAN],
    }),
    getNpTestPlanMeasureCatalog: builder.query<NpTestPlanMeasure[], NpTestPlanModality>({
      query: (modality) => ({ url: '/lt/v1/np-testing/measures', params: { modality: toApiModality(modality) } }),
      transformResponse: ({ test_measures }: ApiTestMeasureListResponse) => test_measures.map(toNpTestPlanMeasure),
    }),
    createNpTestPlan: builder.mutation<NpTestPlanSummary, CreateNpTestPlanArgs>({
      // The plan starts from the standard battery's measures, already filtered by the member's age and grade.
      queryFn: async (args, _api, _extraOptions, baseQuery) => {
        // The caller shows its own toast, with the backend's message, when the plan cannot be created.
        const batteryResult = await baseQuery({
          url: `/lt/v1/episodes/${args.episodeId}/standard-battery`,
          params: {
            condition: args.conditionId,
            modality: toApiModality(args.modality),
            grade_level: toApiGradeLevel(args.gradeLevel),
          },
          showAlert: false,
        })
        if (batteryResult.error) {
          return { error: batteryResult.error }
        }
        const { battery } = batteryResult.data as ApiStandardBatteryResponse
        const plan: ApiTestPlanInput = {
          modality: toApiModality(args.modality),
          age: args.patientAge,
          condition: args.conditionId,
          grade_level: toApiGradeLevelValue(args.gradeLevel),
          battery_id: battery.id,
          uses_standard_battery: true,
          measures: toApiMeasures(battery.test_measures.map(({ id }) => ({ measureId: id }))),
        }
        const result = await baseQuery({
          method: 'post',
          url: testPlanUrl(args.episodeId),
          data: plan,
          showAlert: false,
        })
        return result.error ? { error: result.error } : { data: toSummary(result.data) }
      },
      invalidatesTags: [RTKQueryTag.NP_TEST_PLAN],
    }),
    updateNpTestPlanMeasures: builder.mutation<NpTestPlanSummary, UpdateNpTestPlanMeasuresArgs>({
      query: ({ episodeId, measures }) => ({
        method: 'post',
        url: testPlanUrl(episodeId),
        data: { measures: toApiMeasures(measures) } satisfies ApiTestPlanInput,
        showAlert: false,
      }),
      transformResponse: toSummary,
      invalidatesTags: [RTKQueryTag.NP_TEST_PLAN],
    }),
    duplicateNpTestPlanContactGroup: builder.mutation<void, DuplicateNpTestPlanContactGroupArgs>({
      query: ({ planId, sourceGroupId, contactGroupId }) => ({
        method: 'post',
        url: contactGroupsUrl(planId),
        data: { source_group_id: sourceGroupId, contact_group_id: contactGroupId },
        showAlert: false,
      }),
      invalidatesTags: [RTKQueryTag.NP_TEST_PLAN],
    }),
    removeNpTestPlanContactGroup: builder.mutation<void, RemoveNpTestPlanContactGroupArgs>({
      query: ({ planId, contactGroupId }) => ({
        method: 'delete',
        url: `${contactGroupsUrl(planId)}/${contactGroupId}`,
        showAlert: false,
      }),
      invalidatesTags: [RTKQueryTag.NP_TEST_PLAN],
    }),
  }),
})

export const {
  useGetNpTestPlanSummaryQuery,
  useGetNpTestPlanMeasureCatalogQuery,
  useCreateNpTestPlanMutation,
  useUpdateNpTestPlanMeasuresMutation,
  useDuplicateNpTestPlanContactGroupMutation,
  useRemoveNpTestPlanContactGroupMutation,
} = npTestPlansApi
export default npTestPlansApi
